import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

const STORAGE_KEY = "zt_pwa_installed";
const STORAGE_KEY_AT = "zt_pwa_installed_at";
// Chrome can spuriously fire `beforeinstallprompt` shortly after a successful
// install (especially in dev mode when the SW updates). Ignore such events
// for this many ms after we wrote the installed flag.
const RECENT_INSTALL_COOLDOWN_MS = 30000;

const readInstalledFlag = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const wasRecentlyInstalled = () => {
  try {
    const at = parseInt(localStorage.getItem(STORAGE_KEY_AT) || "0", 10);
    return at > 0 && Date.now() - at < RECENT_INSTALL_COOLDOWN_MS;
  } catch {
    return false;
  }
};

const writeInstalledFlag = (val) => {
  try {
    if (val) {
      localStorage.setItem(STORAGE_KEY, "true");
      localStorage.setItem(STORAGE_KEY_AT, String(Date.now()));
    } else {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY_AT);
    }
  } catch {
    /* ignore */
  }
};

const DownloadIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const LaunchIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const Spinner = () => (
  <span
    aria-label="Checking install state"
    className="inline-block w-4 h-4 border-2 border-[#3AB1F6] border-t-transparent rounded-full animate-spin"
  />
);

const isChromiumBrowser = () => {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  return /Chrome|Edg|Chromium/.test(ua) && !/Firefox/.test(ua);
};

const WebAppPWAControls = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  // Trust localStorage as the initial state. For users who have installed
  // before, this gives instant "Open in App" on reload with no flash.
  // If the flag is stale (user uninstalled), `beforeinstallprompt` will fire
  // within ~3 seconds and downgrade us to "Install App".
  const [isInstalled, setIsInstalled] = useState(readInstalledFlag);
  const [isInPWA, setIsInPWA] = useState(false);
  // Show a loader only when we have no hint at all (no flag, Chromium).
  // Non-Chromium browsers can't install PWAs — resolve immediately.
  const [verified, setVerified] = useState(
    () => readInstalledFlag() || !isChromiumBrowser()
  );

  // True if `appinstalled` fired in THIS session, or the API confirmed install.
  // Initialized from localStorage so we don't overwrite the flag mid-verification.
  const sessionInstalledRef = useRef(readInstalledFlag());
  // True once Chrome has fired beforeinstallprompt for this page load.
  const beforeInstallFiredRef = useRef(false);
  // Synchronous mirror of deferredPrompt state — lets handleInstall read the
  // latest event without waiting on React's async state update cycle.
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsInPWA(standalone);
    if (standalone) return;

    // Pick up any beforeinstallprompt event that fired before React mounted.
    // If we recently installed, Chrome may be spuriously refiring the event
    // — keep the prompt available for click but don't override our state.
    if (window.__deferredPWAInstallPrompt) {
      const earlyEvent = window.__deferredPWAInstallPrompt;
      deferredPromptRef.current = earlyEvent;
      setDeferredPrompt(earlyEvent);
      if (!wasRecentlyInstalled()) {
        beforeInstallFiredRef.current = true;
        sessionInstalledRef.current = false;
        setIsInstalled(false);
        setVerified(true);
        writeInstalledFlag(false);
      }
    }

    const verifyInstalled = async () => {
      if (typeof navigator.getInstalledRelatedApps !== "function") return;
      try {
        const apps = await navigator.getInstalledRelatedApps();
        const found = apps.some((a) => a.platform === "webapp");

        if (found) {
          sessionInstalledRef.current = true;
          setIsInstalled(true);
          setVerified(true);
          writeInstalledFlag(true);
          setDeferredPrompt(null);
          return;
        }

        if (sessionInstalledRef.current) {
          // We know app is installed (event/heuristic confirmed) but API
          // returned empty — common localhost quirk. Don't downgrade.
          return;
        }

        setIsInstalled(false);
        writeInstalledFlag(false);
        // Don't set verified here — API returning "not found" is unreliable
        // on localhost. Wait for beforeinstallprompt or the heuristic timer.
      } catch {
        /* ignore */
      }
    };

    const onBeforeInstall = (e) => {
      e.preventDefault();
      // Always capture the event so the click handler can prompt later.
      deferredPromptRef.current = e;
      window.__deferredPWAInstallPrompt = e;
      setDeferredPrompt(e);
      // Spurious refire right after install? Ignore the state change.
      if (wasRecentlyInstalled()) return;
      // Chrome only fires this when the app is NOT installed and IS installable.
      // This is the same signal that makes Chrome show the install icon.
      beforeInstallFiredRef.current = true;
      sessionInstalledRef.current = false;
      setIsInstalled(false);
      setVerified(true);
      writeInstalledFlag(false);
    };

    const onAppInstalled = () => {
      sessionInstalledRef.current = true;
      setIsInstalled(true);
      setVerified(true);
      deferredPromptRef.current = null;
      setDeferredPrompt(null);
      writeInstalledFlag(true);
    };

    const onFocus = () => verifyInstalled();
    const onVisibility = () => {
      if (document.visibilityState === "visible") verifyInstalled();
    };

    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      const installed = e.newValue === "true";
      setIsInstalled(installed);
      setVerified(true);
      if (installed) {
        sessionInstalledRef.current = true;
        setDeferredPrompt(null);
      }
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onAppInstalled);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("storage", onStorage);

    verifyInstalled();

    // Verification deadline: just resolves the loader after 1.5 seconds.
    // We deliberately do NOT auto-flip to "installed" here. That heuristic
    // re-poisoned localStorage on every reload, making the stale flag
    // permanent. We trust definitive signals only:
    //   - beforeinstallprompt fires → not installed
    //   - appinstalled fires → installed (writes flag)
    //   - getInstalledRelatedApps returns webapp → installed (writes flag)
    //   - localStorage flag preset from a prior install event
    const verificationTimeout = setTimeout(() => {
      setVerified(true);
    }, 1500);

    const pollInterval = setInterval(() => {
      if (document.visibilityState === "visible") verifyInstalled();
    }, 5000);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
      clearInterval(pollInterval);
      clearTimeout(verificationTimeout);
    };
  }, []);

  if (isInPWA) return null;

  // No verdict yet — show a small loader while we wait for Chrome to fire
  // beforeinstallprompt or for getInstalledRelatedApps() to respond.
  // Worst case resolves at the 1.5s deadline.
  if (!verified) {
    return (
      <div className="ml-auto flex items-center px-4 py-2">
        <Spinner />
      </div>
    );
  }

  const handleInstall = () => {
    // Must be synchronous up to .prompt() — Chrome's user-activation token
    // from this click would expire if we awaited anything first.
    const promptEvent =
      deferredPromptRef.current || window.__deferredPWAInstallPrompt;

    if (!promptEvent) {
      // Chrome hasn't fired beforeinstallprompt yet (engagement criteria not
      // met, recent dismissal/quiet period, etc.). Tell the user where to
      // install from instead of being a silent no-op.
      toast.info(
        "Install isn't ready yet — please use the install icon in Chrome's address bar."
      );
      return;
    }

    promptEvent.prompt();
    // Optimistically mark as installed RIGHT NOW. This protects against
    // Chrome reloading the page during install — userChoice.then() can be
    // killed before it runs. If the user dismisses, we revert below.
    sessionInstalledRef.current = true;
    setIsInstalled(true);
    writeInstalledFlag(true);
    promptEvent.userChoice
      .then(({ outcome }) => {
        if (outcome !== "accepted") {
          // User dismissed — revert the optimistic flag.
          sessionInstalledRef.current = false;
          setIsInstalled(false);
          writeInstalledFlag(false);
        }
      })
      .catch(() => {
        sessionInstalledRef.current = false;
        setIsInstalled(false);
        writeInstalledFlag(false);
      });
    deferredPromptRef.current = null;
    window.__deferredPWAInstallPrompt = null;
    setDeferredPrompt(null);
  };

  // Mirror Chrome's URL bar:
  // - Chrome shows "Open in app" icon → render "Open in App"
  if (isInstalled) {
    const handleOpenInApp = (e) => {
      e.preventDefault();
      const url = `${window.location.origin}/app?source=open-in-app&t=${Date.now()}`;
      window.open(url, "_blank", "noopener,noreferrer");
    };
    return (
      <div className="ml-auto flex items-center">
        <a
          href="/app"
          onClick={handleOpenInApp}
          className="inline-flex items-center gap-2 rounded-full bg-[#3AB1F6] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 no-underline"
          aria-label="Open in installed app"
          title="Open in installed app"
        >
          <LaunchIcon />
          <span className="hidden sm:inline">Open in App</span>
        </a>
      </div>
    );
  }

  // - Otherwise (not installed): always show "Install App"
  return (
    <div className="ml-auto flex items-center">
      <button
        type="button"
        onClick={handleInstall}
        style={{ background: "linear-gradient(90deg, #3AB1F6 0%, #407BFF 100%)" }}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
        aria-label="Install Zero Typing AI app"
        title="Install Zero Typing AI"
      >
        <DownloadIcon />
        <span className="hidden sm:inline">Install App</span>
      </button>
    </div>
  );
};

export default WebAppPWAControls;
